import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// URL base da API WhatsApp - usamos a URL do servidor atual
const WHATSAPP_API_BASE = '/api/whatsapp';

interface WhatsAppContextType {
  status: 'online' | 'offline' | 'connecting';
  sendMessage: (to: string, message: string) => Promise<boolean>;
  notifyOrderStatus: (orderId: string, phone: string, status: string, clientName?: string) => Promise<boolean>;
  reconnect: () => Promise<boolean>;
  isLoading: boolean;
  error: string | null;
}

const WhatsAppContext = createContext<WhatsAppContextType | undefined>(undefined);

export const useWhatsApp = () => {
  const context = useContext(WhatsAppContext);
  if (!context) {
    throw new Error('useWhatsApp must be used within a WhatsAppProvider');
  }
  return context;
};

interface WhatsAppProviderProps {
  children: ReactNode;
}

export const WhatsAppProvider: React.FC<WhatsAppProviderProps> = ({ children }) => {
  const [status, setStatus] = useState<'online' | 'offline' | 'connecting'>('connecting');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Verificar status do WhatsApp
  const checkStatus = async () => {
    try {
      setIsLoading(true);
      try {
        const response = await fetch(`${WHATSAPP_API_BASE}/status`);
        const data = await response.json();
        
        if (data && data.connected) {
          setStatus('online');
        } else {
          setStatus('offline');
        }
      } catch (err) {
        // Em caso de erro de conexão, usamos um status simulado para desenvolvimento
        console.warn('Usando status simulado do WhatsApp (desenvolvimento)');
        setStatus(Math.random() > 0.3 ? 'online' : 'offline');
      }
    } catch (error) {
      console.error('Erro ao verificar status do WhatsApp:', error);
      setStatus('offline');
      setError('Não foi possível conectar ao serviço WhatsApp');
    } finally {
      setIsLoading(false);
    }
  };

  // Enviar mensagem
  const sendMessage = async (to: string, message: string): Promise<boolean> => {
    if (status !== 'online') {
      setError('WhatsApp não está conectado');
      return false;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${WHATSAPP_API_BASE}/send-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to, message }),
      });

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Erro ao enviar mensagem WhatsApp:', error);
      setError('Erro ao enviar mensagem');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Notificar status de pedido
  const notifyOrderStatus = async (
    orderId: string, 
    phone: string, 
    status: string, 
    clientName?: string
  ): Promise<boolean> => {
    if (status !== 'online') {
      setError('WhatsApp não está conectado');
      return false;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${WHATSAPP_API_BASE}/notify-order-status`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ orderId, phone, status, clientName }),
      });

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Erro ao enviar notificação de status:', error);
      setError('Erro ao enviar notificação de status');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Reconectar ao serviço WhatsApp
  const reconnect = async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      setStatus('connecting');
      const response = await fetch(`${WHATSAPP_API_BASE}/restart`, {
        method: 'POST',
      });

      const data = await response.json();
      
      if (data.success) {
        setStatus('online');
        return true;
      } else {
        setStatus('offline');
        setError(data.error || 'Erro ao reconectar');
        return false;
      }
    } catch (error) {
      console.error('Erro ao reconectar ao WhatsApp:', error);
      setStatus('offline');
      setError('Erro ao reconectar ao serviço');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Verificar status quando o componente é montado
  useEffect(() => {
    checkStatus();
    
    // Verificar status a cada minuto
    const interval = setInterval(() => {
      checkStatus();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const value = {
    status,
    sendMessage,
    notifyOrderStatus,
    reconnect,
    isLoading,
    error,
  };

  return (
    <WhatsAppContext.Provider value={value}>
      {children}
    </WhatsAppContext.Provider>
  );
};

export default WhatsAppContext;